import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import mapLogo from "../../assets/images/mapLogo.png";

import {
  CONTACT_EMAIL,
  DEVELOPER_GITHUB_URL,
  DEVELOPER_LINKEDIN_URL,
  DEVELOPER_X_URL,
  LINKEDIN_URL,
  YOUTUBE_URL,
  FACEBOOK_URL,
} from "../../constants/links";
import { EmailIcon, FacebookIcon, GitHubIcon, LinkedInIcon, XIcon, YouTubeIcon } from "../icons/SocialMediaIcons";

function FooterSection() {
  const { t } = useTranslation();

  return (
    <footer className="homeFooter u-fullBleed" role="contentinfo">
      <div className="homeFooter__inner">
        {/* Left side of the footer: the project name, logo, and short description. */}
        <div className="homeFooter__brand">
          {/* Logo and title area */}
          <div className="homeFooter__brandTop">
            <img
              src={mapLogo}
              alt={t("footer.logoAlt")}
              className="homeFooter__logo"
            />

            <div>
              <div className="homeFooter__title">
                {t("footer.title")}
              </div>

              <div className="homeFooter__tagline">
                {t("footer.tagline")}
              </div>
            </div>
          </div>

          {/* Short description of what the project does. */}
          <p className="homeFooter__blurb">
            {t("footer.blurb")}
          </p>

          {/* Social and contact icons for the project/community accounts. */}
          <div
            className="homeFooter__social"
            aria-label={t("footer.socialAria")}
          >
            {/* Email link */}
            <a
              className="homeFooter__socialLink homeFooter__socialLink--email"
              href={`mailto:${CONTACT_EMAIL}`}
              onClick={(e) => {
                window.open(
                  `https://mail.google.com/mail/?view=cm&fs=1&to=${CONTACT_EMAIL}`
                );
                e.preventDefault();
              }}
              aria-label={t("footer.socialEmail")}
              title={t("footer.socialEmail")}
            >
              <EmailIcon/>
            </a>

            {/* YouTube link */}
            <a
              className="homeFooter__socialLink homeFooter__socialLink--youtube"
              href={YOUTUBE_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={t("footer.socialYouTube")}
              title={t("footer.socialYouTube")}
            >
              <YouTubeIcon/>
            </a>

            {/* LinkedIn link */}
            <a
              className="homeFooter__socialLink homeFooter__socialLink--linkedin"
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={t("footer.socialLinkedIn")}
              title={t("footer.socialLinkedIn")}
            >
              <LinkedInIcon/>
            </a>

            {/* Facebook link */}
            <a
              className="homeFooter__socialLink homeFooter__socialLink--facebook"
              href={FACEBOOK_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={t("footer.socialFacebook")}
              title={t("footer.socialFacebook")}
            >
              <FacebookIcon/>
            </a>
          </div>
        </div>

        {/* Right side of the footer: quick links and tool access. */}
        <div className="homeFooter__links">
          {/* Quick page links such as About, Team, Partners, and Contact. */}
          <div className="homeFooter__col">
            <div className="homeFooter__colTitle">
              {t("footer.quickLinks")}
            </div>

            <div className="homeFooter__colLinks">
              <a className="homeFooter__link" href="#about">
                {t("footer.linkAbout")}
              </a>

              <a className="homeFooter__link" href="#team">
                {t("footer.linkTeam")}
              </a>

              <a className="homeFooter__link" href="#partners">
                {t("footer.linkPartners")}
              </a>

              <a
                className="homeFooter__link"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                {t("footer.linkContact")}
              </a>
            </div>
          </div>

          {/* Link that takes users directly to the app's login or tool area. */}
          <div className="homeFooter__col">
            <div className="homeFooter__colTitle">
              {t("footer.tool")}
            </div>

            <div className="homeFooter__colLinks">
              <Link className="homeFooter__link" to="/auth">
                {t("footer.linkUseTool")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip containing the copyright notice and developer credit. */}
      <div className="homeFooter__bottom">
        <div className="homeFooter__bottomInner">
          {/* Copyright notice stays on the left. */}
          <div className="homeFooter__bottomText homeFooter__copyright">
            {t("footer.copyright", {
              year: new Date().getFullYear(),
            })}
          </div>

          {/* Developer credit stays on the right with profile icons. */}
          <div
            className="homeFooter__developerStrip"
            aria-label={t("footer.developerLinksAria")}
          >
            <div className="homeFooter__bottomText homeFooter__developerText">
              {t("footer.developerLabel")}{" "}{t("footer.developedBy")}
            </div>

            <div className="homeFooter__developerIcons">
              <a
                className="homeFooter__socialLink homeFooter__socialLink--developer"
                href={DEVELOPER_GITHUB_URL}
                target="_blank"
                rel="me noreferrer"
                aria-label="GitHub profile"
                title="GitHub profile"
              >
                <GitHubIcon/>
              </a>

              <a
                className="homeFooter__socialLink homeFooter__socialLink--developer"
                href={DEVELOPER_X_URL}
                target="_blank"
                rel="me noreferrer"
                aria-label="X profile"
                title="X profile"
              >
                <XIcon/>
              </a>

              <a
                className="homeFooter__socialLink homeFooter__socialLink--developer"
                href={DEVELOPER_LINKEDIN_URL}
                target="_blank"
                rel="me noreferrer"
                aria-label="LinkedIn profile"
                title="LinkedIn profile"
              >
                <LinkedInIcon/>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterSection;
